<template>
  <Form
    ref="form"
    as="div"
    class="flow__body row container"
    id="createPowerOfAttorney"
    data-test-component="createPowerOfAttorney"
    v-cloak
  >
    <div class="small-12 medium-12 large-6 columns">
      <h1 v-html="vueModel.LabelPageTitle" />
      <div v-html="vueModel.TextDisclaimer" />

      <section>
        <div class="row columns">
          <h2 v-html="vueModel.LabelInfoTitle" />
        </div>

        <div class="row medium-unstack">
          <div class="columns">
            <LabelAndTooltip
              :label="vueModel.LabelGetsAccessTo"
              :tooltip="vueModel.TooltipGetsAccessTo"
            />
          </div>
          <div class="columns">
            <fieldset class="radio-inline">
              <div v-for="al in vueModel.AccessLevels" :key="al.Id">
                <!-- Disabling on accesslevel 1000002 is temporary and should be removed when read access has been implemented-->
                <Field
                  type="radio"
                  class="poa-scenario-a"
                  v-bind="al"
                  name="accessLevel"
                  checked
                  :id="al.Id"
                  label="Adgangsniveau"
                  :disabled="al.Id === 1000001"
                />
                <label :for="al.Id">{{ al.Description }}</label>
              </div>
            </fieldset>
          </div>
        </div>

        <div class="row medium-unstack">
          <div class="small-12 columns">
            <label for="firstName" v-html="vueModel.LabelFirstName" />
            <Field
              type="text"
              name="firstName"
              id="firstName"
              label="Fornavn"
              :rules="'required'"
              v-model="firstName"
            />
            <ValidationError name="firstName" data-test-error="firstName" />
          </div>
          <div class="small-12 columns">
            <label for="lastName" v-html="vueModel.LabelLastName" />
            <Field
              type="text"
              name="lastName"
              id="lastName"
              label="Efternavn"
              :rules="'required'"
              v-model="lastName"
            />
            <ValidationError name="lastName" data-test-error="lastName" />
          </div>
        </div>

        <div class="row medium-unstack">
          <div class="small-12 columns">
            <CprNumberField
              v-model="cprNumber"
              label="CPR-nummer"
              :cpr-label="vueModel.LabelCpr"
              :cpr-tooltip="vueModel.TooltipCpr"
              :repeat-cpr-number="true"
              :repeat-cpr-label="vueModel.LabelRepeatCpr"
              :required="true"
              :repeat-cpr-validation-message-required="vueModel.ValidationMessageRepeatCprRequired"
              :repeat-cpr-validation-message-no-match="vueModel.ValidationMessageRepeatCprNoMatch"
            />
          </div>
        </div>

        <div class="row medium-unstack">
          <div class="columns">
            <LabelAndTooltip :label="vueModel.LabelMail" :tooltip="vueModel.TooltipMail" />
            <Field
              type="email"
              v-model="email"
              label="Email"
              name="email"
              placeholder="example@example.com"
              :rules="'required|email'"
            />
            <ValidationError
              name="email"
              data-test-error="email"
              :overrides="{ required: 'Email skal være en gyldig email.' }"
            />

            <div class="checkbox-inline">
              <Field
                id="acceptAccessTerms"
                :value="true"
                :unchecked-value="false"
                v-model="acceptAccessTerms"
                label="vilkårene"
                name="acceptAccessTerms"
                type="checkbox"
                :rules="'required'"
                :disabled="accessLevel == null"
              />
              <label for="acceptAccessTerms">
                <strong v-html="vueModel.LocalContent.LabelAcceptTermsTitle" />
              </label>
              <div
                v-if="accessLevel === 1000002"
                v-html="vueModel.LocalContent.TextAcceptTermsReadAndWrite"
              />
              <div
                v-if="accessLevel === 1000001"
                v-html="vueModel.LocalContent.TextAcceptTermsRead"
              />
            </div>
            <ValidationError name="acceptAccessTerms" data-test-error="acceptAccessTerms" />

            <div class="checkbox-inline">
              <Field
                id="acceptCprTerms"
                :value="true"
                :unchecked-value="false"
                v-model="acceptCprTerms"
                label="samtykke"
                name="acceptCprTerms"
                type="checkbox"
                :rules="'required'"
                :disabled="accessLevel == null"
              />
              <label for="acceptCprTerms">
                <strong v-html="vueModel.LocalContent.LabelConsentToCprTitle" />
              </label>
              <div v-html="vueModel.LocalContent.TextConsentToCprDescription" />
            </div>
            <ValidationError name="acceptCprTerms" data-test-error="acceptCprTerms" />

            <h3 v-html="vueModel.LocalContent.LabelGDPRTitle" />
            <span v-html="vueModel.LocalContent.TextGDPRDescription" />
          </div>
        </div>

        <div class="row push-up">
          <div class="small-12 medium-6 columns">
            <button @click="redirect" class="button secondary" v-html="vueModel.LabelCancel" />
          </div>
          <div class="small-12 medium-6 columns">
            <button @click="submitForm" class="button" :disabled="saving">
              <span v-html="vueModel.LabelAddPOA" />
              <div class="spinner spinner-button" v-show="saving" />
            </button>
          </div>
        </div>
      </section>
    </div>

    <transition name="modal" v-cloak>
      <v-modal :is-open="showModalSuccess" @close="closeModalAndRedirect">
        <template #title>
          <h3 v-html="vueModel.LabelCreateTitle" />
        </template>

        <template #content>
          <span v-html="vueModel.TextCreateDescription" /><br /><br />
          <a
            @click="closeModalAndRedirect"
            class="button large close-modal"
            v-html="vueModel.LabelCloseModalButton"
          />
        </template>

        <template #footer />
      </v-modal>
    </transition>

    <transition name="modal">
      <v-modal :is-open="showModalError" @close="closeModalAndStay">
        <template #title>
          <h3 v-html="vueModel.LocalContent.LabelUnknownError" />
        </template>

        <template #content>
          <p v-html="errorMessage" />
          <a
            @click="closeModalAndStay"
            class="button large close-modal"
            v-html="vueModel.LabelCloseModalButton"
          />
        </template>

        <template #footer />
      </v-modal>
    </transition>

    <transition name="modal">
      <v-modal :is-open="showModalFail" @close="closeModalAndStay">
        <template #title>
          <h3 v-html="vueModel.LocalContent.LabelUnknownError" />
        </template>
        <template #content>
          <div>
            <span v-html="vueModel.LocalContent.TextUnknownError" />
            <a
              @click="closeModalAndStay"
              class="button large close-modal"
              v-html="vueModel.LabelCloseModalButton"
            />
          </div>
        </template>
        <template #footer />
      </v-modal>
    </transition>

    <transition name="modal">
      <v-modal
        :is-open="cprCheckMismatch"
        data-test="quickquote-cpr-mismatch-modal"
        @close="toggleCprMismatchModal"
      >
        <template #title>
          <h2 class="center" v-html="vueModel.LabelCprErrorTitle" />
        </template>

        <template #content>
          <div v-html="cprErrorText" />
        </template>

        <template #buttons>
          <div class="row">
            <div class="small-12 columns">
              <button
                class="button text-center"
                @click="toggleCprMismatchModal"
                v-html="vueModel.LabelCprErrorCloseButton"
              />
            </div>
          </div>
        </template>
      </v-modal>
    </transition>
  </Form>
</template>

<script>
import Utility from '@/common/UtilityFunctions.js';

import axios from 'axios';

import CprNumberField from '@/components/UI/CprNumberField.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import { Form } from 'vee-validate';

export default {
  name: 'CreatePowerOfAttorney',
  props: ['vue-model'],
  mounted() {},
  components: {
    Form,
    CprNumberField,
    ValidationError
  },
  data() {
    return {
      accessLevel: 1000002,
      mockAddressFound: false,
      cprNumber: null,
      firstName: '',
      lastName: '',
      email: null,
      acceptAccessTerms: false,
      acceptCprTerms: false,
      saving: false,
      showModalSuccess: false,
      showModalError: false,
      showModalFail: false,
      errorMessage: '',
      files: null,
      cprCheckMismatch: false,
      cprErrorText: 'Test'
    };
  },
  methods: {
    submitForm() {
      this.$refs.form.validate().then((result) => {
        if (result.valid) {
          this.saving = true;

          axios
            .post(Utility.constants().API.power_of_attorney_create, {
              Email: this.email,
              FirstName: this.firstName,
              LastName: this.lastName,
              CprNumber: this.cprNumber,
              AccessLevel: this.accessLevel,
              AcceptAccessTerms: this.acceptAccessTerms,
              AcceptCprTerms: this.acceptCprTerms
            })
            .then(() => {
              this.saving = false;
              this.showModalSuccess = true;
            })
            .catch((error) => {
              this.saving = false;
              switch (error.response.status) {
                case 400:
                  var responseObj = JSON.parse(response.responseJSON);
                  this.errorMessage = responseObj.Description;
                  this.showModalError = true;
                  break;
                case 401:
                  this.cprErrorText = this.vueModel.LabelCprTimeout;
                  this.cprCheckMismatch = true;
                  break;
                case 406:
                  this.cprErrorText = this.vueModel.LabelCprMismatch;
                  this.cprCheckMismatch = true;
                  break;
                case 500:
                  this.showModalFail = true;
                  break;
                default:
                  this.showModalFail = true;
              }
            });
        }
      });
    },
    updateQuery(params) {
      this.query = params;
    },
    isEmpty(params) {
      if (params) {
        this.query = null;
      }
    },
    redirect() {
      window.location = this.vueModel.RedirectUrl + '#powerOfAttorney';
    },
    closeModalAndRedirect() {
      this.showModalSuccess = false;
      this.showModalFail = false;
      window.location = this.vueModel.RedirectUrl + '#powerOfAttorney';
    },
    closeModalAndStay() {
      this.showModalSuccess = false;
      this.showModalFail = false;
      this.showModalError = false;
    },
    toggleCprMismatchModal() {
      this.cprCheckMismatch = false;
    }
  }
};
</script>
